import { useState, useEffect, useCallback, useRef } from "react";
import { Client } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import api from "../../utils/api";
import { useAuth } from "../../context/AuthContext";
import { baseUrl } from "../../config"; // same baseUrl used in ChatWindow.js — adjust path if different

function generateId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

function formatTime(unixSeconds) {
  if (!unixSeconds) return "";
  return new Date(unixSeconds * 1000).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function isSameCalendarWeek(dateA, dateB) {
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const startOfWeek = (d) => {
    const s = startOfDay(d);
    const day = s.getDay(); // 0=Sun, 1=Mon, ... 6=Sat
    const diffToMonday = day === 0 ? -6 : 1 - day;
    s.setDate(s.getDate() + diffToMonday);
    return s;
  };
  return startOfWeek(dateA).getTime() === startOfWeek(dateB).getTime();
}

function formatChatListTime(unixSeconds) {
  if (!unixSeconds) return "";

  const msgDate = new Date(unixSeconds * 1000);
  const now = new Date();

  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((startOfDay(now) - startOfDay(msgDate)) / 86400000);

  if (diffDays === 0) {
    return msgDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  if (diffDays === 1) return "Yesterday";
  if (diffDays > 1 && isSameCalendarWeek(msgDate, now)) {
    return msgDate.toLocaleDateString([], { weekday: "long" });
  }

  const dd = String(msgDate.getDate()).padStart(2, "0");
  const mm = String(msgDate.getMonth() + 1).padStart(2, "0");
  const yy = String(msgDate.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
}
export function useChat({ pendingLeadId, onPendingLeadHandled } = {}) {
  const { userId } = useAuth();
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [allMessages, setAllMessages] = useState({});
  const [loadingChats, setLoadingChats] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [error, setError] = useState(null);

  const stompClientRef = useRef(null);
  const activeChatIdRef = useRef(null); // lets WS callbacks read the *current* activeChat without stale closures

  // NEW: single source of truth for "is the STOMP connection up right now".
  // Previously, both the sidebar-subscription effect and the active-chat
  // subscription effect independently did `client.onConnect = subscribe`
  // whenever the socket wasn't connected yet. Since `onConnect` is a single
  // property (not a list of listeners), whichever effect ran second SILENTLY
  // OVERWROTE the first one's handler — so only one of the two ever actually
  // got its subscriptions registered once the socket finished connecting.
  // That's why /topic/chats/sidebar never showed a SUBSCRIBE frame: the
  // active-chat effect's onConnect assignment clobbered the sidebar effect's.
  // Fixing this by tracking connection status in state, and having each
  // subscription effect depend on that state instead of racing to set
  // client.onConnect itself.
  const [wsConnected, setWsConnected] = useState(false);

  const selectChat = useCallback((chat) => {
    setActiveChat(chat);
    setChats((prev) =>
      prev.map((c) => (c.id === chat.id ? { ...c, unread: 0 } : c))
    );
    api.post(`/crm/whatsapp/chat/mark-read/${chat.id}`).catch((err) => {
      console.error("Failed to mark chat as read:", err);
    });
  }, []);

  // Keep a ref in sync so WS subscription callbacks (set up once) always see
  // the latest activeChat id without needing to resubscribe.
  useEffect(() => {
    activeChatIdRef.current = activeChat?.id ?? null;
  }, [activeChat?.id]);

  // --- Auto-select chat handed off from Leads ---
  useEffect(() => {
    if (!pendingLeadId || chats.length === 0) return;

    const match = chats.find((c) => Number(c.leadId) === Number(pendingLeadId));
    if (match) {
      selectChat(match);
      onPendingLeadHandled?.();

      // Move the opened chat to the top of the sidebar list
      setChats((prev) => {
        const idx = prev.findIndex((c) => c.id === match.id);
        if (idx <= 0) return prev; // already at top, or not found
        const next = [...prev];
        const [item] = next.splice(idx, 1);
        next.unshift(item);
        return next;
      });
    }
  }, [pendingLeadId, chats, selectChat, onPendingLeadHandled]);

  // --- Fetch chat list on mount / whenever userId becomes available ---
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    async function fetchChats() {
      setLoadingChats(true);
      setError(null);
      try {
        const res = await api.get(`/crm/whatsapp/my-chats/${userId}`);
        if (cancelled) return;

        const list = (res.data?.data ?? []).map((c) => ({
          ...c,
          lastMsg: c.lastMsg ?? "",
          time: formatChatListTime(c.time) ?? "",
          unread: c.unread ?? 0,
        }));

        setChats(list);
        setActiveChat((prev) => {
          if (prev) return prev;
          if (pendingLeadId) return null; // let the match effect decide
          return list[0] ?? null;
        });
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoadingChats(false);
      }
    }

    fetchChats();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // --- Fetch messages whenever the active chat changes ---
  useEffect(() => {
    if (!activeChat?.mobile) return;
    let cancelled = false;

    async function fetchMessages() {
      setLoadingMessages(true);
      try {
        const res = await api.get("/crm/whatsapp/chat/messages", {
          params: {
            leadPhoneNumber: activeChat.mobile.replace(/^\+/, ""),
            offset: 0,
            limit: 20,
          },
        });
        if (cancelled) return;

        const raw = res.data?.data ?? [];
        const normalized = raw
          .slice()
          .reverse()
          .map((m) => ({
            ...m,
            rawTime: m.time,
            time: formatTime(m.time),
            status: m.status === "null" ? undefined : m.status,
          }));

        setAllMessages((prev) => ({ ...prev, [activeChat.id]: normalized }));
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoadingMessages(false);
      }
    }

    fetchMessages();
    return () => {
      cancelled = true;
    };
  }, [activeChat?.id, activeChat?.mobile]);

  // --- WS: open ONE persistent STOMP connection for the whole session ---
  useEffect(() => {
    const client = new Client({
      webSocketFactory: () => new SockJS(`${baseUrl}/ws`),
      reconnectDelay: 5000, // auto-reconnect if connection drops
      onConnect: () => setWsConnected(true), // NEW — single place that flips connection state on
      onDisconnect: () => setWsConnected(false), // NEW — and off, so effects can re-subscribe after a reconnect too
      onStompError: (frame) => {
        console.error("STOMP error:", frame);
      },
    });

    client.activate();
    stompClientRef.current = client;

    return () => {
      client.deactivate();
      stompClientRef.current = null;
      setWsConnected(false);
    };
  }, []);

  // --- WS: sidebar-wide subscription (updates chats you're NOT currently viewing) ---
  useEffect(() => {
    const client = stompClientRef.current;
    if (!client || !wsConnected) return; // CHANGED: depend on wsConnected instead of setting client.onConnect

    const sidebarSub = client.subscribe("/topic/chats/sidebar", (frame) => {
      const update = JSON.parse(frame.body); // { leadId, lastMsg, time }

      setChats((prev) =>
        prev.map((c) =>
          Number(c.id) === Number(update.chatId)
            ? {
              ...c,
              lastMsg: update.lastMsg,
              time: formatChatListTime(update.time),
              // Use the server's real count, but don't show it as unread
              // if this chat is the one currently open (already being read)
              unread:
                Number(c.id) === Number(activeChatIdRef.current)
                  ? 0
                  : update.unreadCount ?? (c.unread ?? 0) + 1,
            }
            : c
        )
      );
    });

    return () => sidebarSub.unsubscribe();
  }, [wsConnected]); // CHANGED: re-subscribe whenever connection (re)establishes

  // --- WS: subscribe to the active chat's message + status topics ---
  useEffect(() => {
    const client = stompClientRef.current;
    if (!client || !wsConnected || !activeChat?.id) return; // CHANGED: depend on wsConnected instead of setting client.onConnect

    const msgSub = client.subscribe(`/topic/chat/${activeChat.id}`, (frame) => {
      const incoming = JSON.parse(frame.body);
      setAllMessages((prev) => ({
        ...prev,
        [activeChat.id]: [
          ...(prev[activeChat.id] ?? []),
          { ...incoming, rawTime: incoming.time, time: formatTime(incoming.time) },
        ],
      }));
      api.post(`/crm/whatsapp/chat/mark-read/${activeChat.id}`).catch((err) => {
        console.error("Failed to mark chat as read:", err);
      });
    });

    const statusSub = client.subscribe(`/topic/chat/${activeChat.id}/status`, (frame) => {
      const { messageId, status, errorCode, errorMessage } = JSON.parse(frame.body);
      setAllMessages((prev) => ({
        ...prev,
        [activeChat.id]: (prev[activeChat.id] ?? []).map((m) =>
          m.id === messageId ? { ...m, status, errorCode, errorMessage } : m
        ),
      }));
    });

    return () => {
      msgSub.unsubscribe();
      statusSub.unsubscribe();
    };
  }, [activeChat?.id, wsConnected]); // CHANGED: added wsConnected

  const messages = activeChat ? allMessages[activeChat.id] ?? [] : [];

  const sendMessage = useCallback(
    async (text) => {
      if (!activeChat || !text.trim()) return;

      const trimmed = text.trim();
      const now = new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });

      const nowUnix = Math.floor(Date.now() / 1000);
      const tempId = generateId();
      const newMsg = {
        id: tempId,
        type: "text",
        text: trimmed,
        rawTime: nowUnix,
        time: now,
        fromMe: true,
        status: "sending",
      };

      setAllMessages((prev) => ({
        ...prev,
        [activeChat.id]: [...(prev[activeChat.id] ?? []), newMsg],
      }));

      setChats((prev) =>
        prev.map((c) =>
          c.id === activeChat.id ? { ...c, lastMsg: trimmed, time: formatChatListTime(nowUnix) } : c
        )
      );

      try {
        const res = await api.post("/crm/whatsapp/message", {
          sendTo: activeChat.mobile.replace(/^\+/, ""),
          body: trimmed,
        });

        const realId = res.data?.data?.localMessageId;
        if (!realId) {
          console.warn("No localMessageId in send response — status updates for this message won't match", res.data);
        }

        setAllMessages((prev) => ({
          ...prev,
          [activeChat.id]: (prev[activeChat.id] ?? []).map((m) =>
            m.id === tempId ? { ...m, id: realId ?? m.id, status: "sent" } : m
          ),
        }));
      } catch (err) {
        console.error("Failed to send message:", err);
        setAllMessages((prev) => ({
          ...prev,
          [activeChat.id]: (prev[activeChat.id] ?? []).map((m) =>
            m.id === tempId ? { ...m, status: "failed" } : m
          ),
        }));
        setError(err);
      }
    },
    [activeChat]
  );

  const sendTemplateMessage = useCallback(
    async ({ templateId, templateName, displayText, leadId, mobile, textVariables }) => {
      if (!activeChat) return;

      const now = new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });

      const tempId = generateId();
      const newMsg = {
        id: tempId,
        type: "text",
        text: displayText,
        time: now,
        fromMe: true,
        status: "sending",
      };

      setAllMessages((prev) => ({
        ...prev,
        [activeChat.id]: [...(prev[activeChat.id] ?? []), newMsg],
      }));

      setChats((prev) =>
        prev.map((c) =>
          c.id === activeChat.id ? { ...c, lastMsg: displayText, time: now } : c
        )
      );

      try {
        const res = await api.post("/crm/whatsapp/template/message", {
          id: templateId,
          templateName,
          sendBy: userId,
          leadId,
          sendToLeadMobile: mobile,
          textVariables,
          mediaUrls: [],
        });

        // NOTE: apply the same real-id swap here as sendMessage once the
        // template-send endpoint also returns localMessageId — otherwise
        // status updates for template messages will hit the same
        // tempId-vs-real-id mismatch we just fixed for plain messages.
        const realId = res.data?.data?.localMessageId;

        setAllMessages((prev) => ({
          ...prev,
          [activeChat.id]: (prev[activeChat.id] ?? []).map((m) =>
            m.id === tempId ? { ...m, id: realId ?? m.id, status: "sent" } : m
          ),
        }));
      } catch (err) {
        console.error("Failed to send template message:", err);
        setAllMessages((prev) => ({
          ...prev,
          [activeChat.id]: (prev[activeChat.id] ?? []).map((m) =>
            m.id === tempId ? { ...m, status: "failed" } : m
          ),
        }));
        setError(err);
        throw err;
      }
    },
    [activeChat, userId]
  );

  return {
    chats,
    activeChat,
    messages,
    selectChat,
    sendMessage,
    sendTemplateMessage,
    loadingChats,
    loadingMessages,
    error,
  };
}